/** Publishing a model version: the server's review of the draft's changes, and the change list it is shown with. */
import { Send } from 'lucide-react';
import type { SchemaChange } from '@prism/contracts';
import { Modal } from '../../../components/Modal';
import { StatusDot } from '../../../components/Status';
import { ErrorBanner } from '../../../components/ErrorBanner';

const SEVERITY: Record<SchemaChange['severity'], { label: string; cls: string }> = {
  safe: { label: 'Safe', cls: 'ready' },
  migrates: { label: 'Migrates values', cls: 'processing' },
  archives: { label: 'Archives values', cls: 'partial' },
};

export interface Review { changes: SchemaChange[]; problems: string[]; stale: boolean }

/** Review & publish: the changes with their impact; archiving stored values needs an acknowledgement. */
export function PublishReview({ version, review, ack, onAck, publishing, onPublish, onClose }: {
  version: number; review: Review; ack: boolean; onAck: (v: boolean) => void; publishing: boolean; onPublish: () => void; onClose: () => void;
}) {
  const archiving = review.changes.reduce((n, c) => n + (c.archivedValues ?? 0), 0);
  return (
    <Modal wide title={`Publish version ${version}`} onClose={onClose}
      actions={<>
        <button className="btn ghost" onClick={onClose}>Keep editing</button>
        <button className="btn primary" data-testid="confirm-publish" disabled={publishing || review.problems.length > 0 || review.stale || (archiving > 0 && !ack)} onClick={onPublish}>
          <Send size={14} /> Publish
        </button>
      </>}>
      {review.stale && <div className="banner error">Someone published a newer version while you were editing. Discard this draft and start again from the latest model.</div>}
      {review.problems.length > 0 && (
        <ErrorBanner title="Fix these before publishing">{review.problems.map((p) => <span key={p}>{p}</span>)}</ErrorBanner>
      )}
      <ChangeList changes={review.changes} withImpact />
      {archiving > 0 && (
        <label className="ack">
          <input type="checkbox" checked={ack} onChange={(e) => onAck(e.target.checked)} data-testid="ack-archiving" />
          <span>I understand that {archiving} stored value{archiving > 1 ? 's' : ''} will move to each asset’s archive. Archived values stay visible on the asset and are never deleted.</span>
        </label>
      )}
    </Modal>
  );
}

export function ChangeList({ changes, withImpact = false }: { changes: SchemaChange[]; withImpact?: boolean }) {
  return (
    <div className="change-list" data-testid="change-list">
      {changes.map((c, i) => (
        <div className="change" key={i}>
          <StatusDot tone={SEVERITY[c.severity].cls}>{SEVERITY[c.severity].label}</StatusDot>
          <div className="change-main">
            <strong>{c.label}</strong>
            <span className="muted">{c.detail}</span>
            {withImpact && (c.affectedAssets ?? 0) > 0 && (
              <span className={c.archivedValues ? 'warn-text' : 'faint'}>
                {c.affectedAssets} asset{c.affectedAssets === 1 ? '' : 's'} affected{c.archivedValues ? ` · ${c.archivedValues} value${c.archivedValues === 1 ? '' : 's'} archived` : ''}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
